import tailwind from "../index.css?inline";

// `:root` doesn't cross the shadow boundary — inside the shadow root the custom
// properties set on `:root` by index.css resolve to nothing. Clone the existing
// `:root` token block as `:host` so every `bg-background`/`text-foreground`
// utility resolves, instead of hand-maintaining a duplicate that drifts when
// designers tweak tokens.
const rootBlock = tailwind.match(/:root\s*\{[^}]*\}/)?.[0];
if (!rootBlock) {
  // Production builds always inline index.css via Vite, so this branch only
  // hits in test environments where `?inline` isn't compiled. Warn rather than
  // throw so element-registration tests keep working — embed in prod is fine.
  console.warn(
    "embed-styles: could not find :root token block in index.css — embed will render unstyled"
  );
}
const hostTokens = rootBlock ? rootBlock.replace(/^:root/, ":host") : "";

// Embed-only host rules: reset `font-family` (inherited properties leak across
// the shadow boundary, so the host page's font would otherwise restyle the
// widget) and force `display: block` (custom elements default to inline).
const HOST_EXTRAS = `:host {
  display: block;
  font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
}`;

// Matches a `rem` length: an optional sign, digits with an optional decimal,
// then `rem` as a standalone unit. `\b` stops it grabbing `rem` inside a longer
// ident (e.g. the `rem` in a `rems`-suffixed token), and requiring a leading
// digit means the bare word `transform` is never touched.
const REM_LENGTH = /(-?\d*\.?\d+)rem\b/g;

// Tailwind compiles arbitrary-value utilities into escaped-bracket selectors,
// e.g. `.min-w-\[8rem\]{...}`. The `rem` in that *selector* must stay verbatim
// so it keeps matching the literal `min-w-[8rem]` class attribute in the DOM —
// only the rem in the *declaration* gets converted.
const ESCAPED_BRACKET = /\\\[[^\]]*?\\\]/g;
// U+FFFF is a permanent Unicode noncharacter: it never appears in valid CSS, so
// it's a collision-proof placeholder. It only exists transiently between mask
// and restore — it never reaches the emitted stylesheet.
const SHIELD = "￿";
const SENTINEL = /￿(\d+)￿/g;

// `rem` always resolves against the host page's <html> font-size — even inside a
// Shadow DOM. novopsych.com (Elementor) sets `html { font-size: 62.5% }` (10px),
// which silently shrinks the whole widget — text AND spacing — to 62.5% of its
// design size. Baking every `rem` to an absolute `px` at a fixed basis makes the
// embed render identically regardless of the host's root font-size. `em` units
// are left alone so the Tailwind `prose` plugin keeps scaling relative to its
// own font-size.
export function remToPx(css: string, basis = 16): string {
  // Shield escaped-bracket selector tokens so their rem survives the rewrite.
  const shielded: string[] = [];
  const masked = css.replace(ESCAPED_BRACKET, (token) => {
    shielded.push(token);
    return `${SHIELD}${shielded.length - 1}${SHIELD}`;
  });

  const converted = masked.replace(REM_LENGTH, (_, value) => {
    // Round to kill float artifacts (e.g. 0.3 * 16) without forcing decimals on
    // clean values (0.875rem -> 14px, not 14.0000px).
    const px = Math.round(Number(value) * basis * 1e4) / 1e4;
    return `${px}px`;
  });

  return converted.replace(SENTINEL, (_, index) => shielded[Number(index)]);
}

export const EMBED_CSS = remToPx(`${tailwind}\n${hostTokens}\n${HOST_EXTRAS}`);
